import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Link2, Trash2, Copy, UserCheck, Eye, Loader2, KeyRound, X, CheckCircle2, MailCheck, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import UserAssignedAssetsModal from '../components/UserAssignedAssetsModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const isSuperadmin = (currentUser?.role || '').toString().toLowerCase() === 'superadmin';
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);

  // New invitation form
  const [invRole, setInvRole] = useState('inversor');
  const [invAsset, setInvAsset] = useState('');
  const [creating, setCreating] = useState(false);

  // Password reset modal state
  const [resetModal, setResetModal] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);
  // Delete user state
  const [deletingFor, setDeletingFor] = useState(null);

  const deleteUser = async (u) => {
    // Confirmación cruda: nombre + email para evitar clicks accidentales.
    const label = u.name ? `${u.name} (${u.email})` : u.email;
    const ok = window.confirm(
      `¿Eliminar permanentemente al usuario ${label}?\n\n` +
      `Esta acción NO se puede deshacer.\n` +
      `Se desasignará automáticamente de todos los activos que tenga como gestor/inversor.\n` +
      `Los registros del audit log permanecen para trazabilidad.`
    );
    if (!ok) return;
    setDeletingFor(u.id);
    try {
      const r = await axios.delete(`${API}/users/${u.id}`, { withCredentials: true });
      const n = r.data?.assets_unassigned || 0;
      toast.success(`${label} eliminado${n ? ` · desasignado de ${n} activo(s)` : ''}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeletingFor(null);
    }
  };

  // Assigned-assets modal
  const [assignedUser, setAssignedUser] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, i, a] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/invitations`, { withCredentials: true }),
        axios.get(`${API}/assets`, { withCredentials: true }),
      ]);
      setUsers(u.data.items ?? u.data);
      setInvitations(i.data.items ?? i.data);
      setAssets((a.data.items || a.data).slice(0, 100));
    } catch {}
    finally { setLoading(false); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success('Rol actualizado');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const createInvitation = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/invitations`, { role: invRole, asset_id: invAsset || null }, { withCredentials: true });
      toast.success('Invitación creada');
      setInvitations(prev => [res.data, ...prev]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setCreating(false); }
  };

  const deleteInv = async (id) => {
    try { await axios.delete(`${API}/invitations/${id}`, { withCredentials: true }); setInvitations(prev => prev.filter(i => i.id !== id)); toast.success('Eliminada'); } catch { toast.error('Error'); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/registro/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Enlace copiado al portapapeles');
  };

  const generateResetLink = async (u) => {
    setGeneratingFor(u.id);
    try {
      const r = await axios.post(`${API}/admin/users/${u.id}/generate-reset-link`, {}, { withCredentials: true });
      setResetModal({
        user: u,
        url: r.data.url,
        email_sent: !!r.data.email_sent,
        email_error: r.data.email_error || null,
        expires_at: r.data.expires_at,
      });
      if (r.data.email_sent) {
        toast.success(`Email enviado a ${u.email}`);
      } else {
        toast.info('Enlace generado. Cópialo y compártelo manualmente.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo generar el enlace');
    } finally {
      setGeneratingFor(null);
    }
  };

  const copyResetUrl = () => {
    if (!resetModal?.url) return;
    navigator.clipboard.writeText(resetModal.url);
    toast.success('Enlace copiado al portapapeles');
  };

  const fmtExpiresAt = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const roleBadge = (role) => {
    if (role === 'superadmin') return 'bg-red-100 text-red-700';
    if (role === 'admin') return 'bg-purple-100 text-purple-700';
    if (role === 'gestor') return 'bg-blue-100 text-blue-700';
    if (role === 'mediador') return 'bg-teal-100 text-teal-700';
    if (role === 'inversor') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <h1 className="heading-1 flex items-center gap-2"><Users className="w-6 h-6 text-blue-600" /> Usuarios e Invitaciones</h1>

      {/* Create invitation */}
      <div className="card-base p-5 space-y-4" data-testid="create-invitation">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Link2 className="w-4 h-4 text-blue-600" /> Crear enlace de invitación</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Rol</label>
            <select value={invRole} onChange={e => setInvRole(e.target.value)} className="input-base" data-testid="inv-role">
              <option value="admin">Admin (operativo global)</option>
              <option value="gestor">Gestor (solo activos asignados)</option>
              <option value="mediador">Mediador (solo lectura, sin proveedor/scoring)</option>
              <option value="inversor">Inversor (solo lectura, asignado y publicado)</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Asignar automáticamente a activo (opcional)</label>
            <select value={invAsset} onChange={e => setInvAsset(e.target.value)} className="input-base" data-testid="inv-asset">
              <option value="">Sin asignación</option>
              {assets.map(a => <option key={a.id} value={a.id}>{a.direccion || a.referencia_catastral || a.id.slice(0,8)}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={createInvitation} disabled={creating} className="btn-primary w-full" data-testid="create-inv-btn">
              {creating ? 'Creando...' : 'Generar enlace'}
            </button>
          </div>
        </div>
      </div>

      {/* Invitations */}
      {invitations.length > 0 && (
        <div className="card-base p-5 space-y-3" data-testid="invitations-list">
          <h3 className="text-sm font-semibold text-slate-700">Invitaciones ({invitations.length})</h3>
          {invitations.map(inv => (
            <div key={inv.id} className={`p-3 rounded-lg border flex items-center justify-between ${inv.used ? 'border-slate-200 bg-slate-50' : 'border-blue-200 bg-blue-50/30'}`} data-testid={`inv-${inv.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadge(inv.role)}`}>
                    {inv.role}
                  </span>
                  {inv.used ? (
                    <span className="text-xs text-emerald-600 font-medium">Usada</span>
                  ) : (
                    <span className="text-xs text-blue-600 font-medium">Activa</span>
                  )}
                  {inv.asset_id && <span className="text-xs text-slate-400">Activo: {inv.asset_id.slice(0,8)}...</span>}
                </div>
                <p className="text-xs text-slate-400 mt-1 font-mono truncate">{window.location.origin}/registro/{inv.token}</p>
              </div>
              <div className="flex items-center gap-1 ml-2">
                {!inv.used && <button onClick={() => copyLink(inv.token)} className="text-blue-500 hover:text-blue-700 p-1.5" title="Copiar enlace"><Copy className="w-4 h-4" /></button>}
                <button onClick={() => deleteInv(inv.id)} className="text-slate-400 hover:text-red-600 p-1.5" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Users table */}
      <div className="card-base overflow-hidden">
        <div className="p-4 border-b border-slate-200"><h3 className="text-sm font-semibold text-slate-700">Usuarios registrados ({users.length})</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Nombre</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Rol</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Cambiar rol</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadge(u.role)}`}>{u.role}</span></td>
                  <td className="px-4 py-3">
                    <select value={u.role} onChange={e => changeRole(u.id, e.target.value)} className="input-base text-xs w-auto" data-testid={`role-select-${u.id}`}>
                      <option value="superadmin">Superadmin</option>
                      <option value="admin">Admin</option>
                      <option value="gestor">Gestor</option>
                      <option value="mediador">Mediador</option>
                      <option value="inversor">Inversor</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isSuperadmin && (
                      <button
                        onClick={() => setAssignedUser(u)}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md text-emerald-700 bg-emerald-50 hover:bg-emerald-100 mr-1"
                        data-testid={`assigned-assets-${u.id}`}
                        title="Ver y gestionar activos asignados"
                      >
                        <Building2 className="w-3.5 h-3.5" />
                        Activos
                      </button>
                    )}
                    <button
                      onClick={() => generateResetLink(u)}
                      disabled={generatingFor === u.id}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                      data-testid={`gen-reset-${u.id}`}
                      title="Generar enlace de recuperación de contraseña"
                    >
                      {generatingFor === u.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <KeyRound className="w-3.5 h-3.5" />}
                      Recuperar contraseña
                    </button>
                    {isSuperadmin && u.id !== currentUser?.id && (u.role || '').toLowerCase() !== 'superadmin' && (
                      <button
                        onClick={() => deleteUser(u)}
                        disabled={deletingFor === u.id}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 ml-1"
                        data-testid={`delete-user-${u.id}`}
                        title="Eliminar usuario permanentemente"
                      >
                        {deletingFor === u.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                        Eliminar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Reset link modal */}
      {resetModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setResetModal(null)}
          data-testid="reset-link-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <KeyRound className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Enlace de recuperación</h3>
                  <p className="text-xs text-slate-500 mt-0.5">para {resetModal.user.email}</p>
                </div>
              </div>
              <button onClick={() => setResetModal(null)} className="p-1 hover:bg-slate-100 rounded" data-testid="reset-modal-close">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {resetModal.email_sent ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700" data-testid="reset-email-sent">
                  <MailCheck className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm">Email enviado a <strong>{resetModal.user.email}</strong>.</p>
                </div>
              ) : (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 text-amber-800" data-testid="reset-email-pending">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">Email no enviado automáticamente</p>
                    <p className="text-xs mt-0.5">Copia el enlace abajo y compártelo manualmente con el usuario.</p>
                    {resetModal.email_error && (
                      <p className="text-[11px] mt-1 text-amber-700 italic">Detalle: {resetModal.email_error}</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Enlace generado</label>
                <div className="mt-1 flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={resetModal.url}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-3 py-2 text-xs font-mono rounded-md border border-slate-200 bg-slate-50 text-slate-700"
                    data-testid="reset-url-input"
                  />
                  <button
                    onClick={copyResetUrl}
                    className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-1.5"
                    data-testid="reset-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copiar
                  </button>
                </div>
              </div>

              <div className="text-xs text-slate-500 space-y-1">
                <p>· Caduca el <strong>{fmtExpiresAt(resetModal.expires_at)}</strong></p>
                <p>· Solo puede usarse una vez</p>
                <p>· Al usarlo se invalidarán enlaces previos generados para este usuario</p>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setResetModal(null)}
                className="px-4 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
                data-testid="reset-modal-dismiss"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      <UserAssignedAssetsModal
        open={!!assignedUser}
        onClose={() => setAssignedUser(null)}
        user={assignedUser}
      />
    </div>
  );
};

export default UsersPage;
